import axios from '@/core/axios';
import { CancelToken } from 'axios';

import { CollectionList } from '../collections/collections.types';
import {
  EntryFlowDevicesPage,
  EntryFlowDevicesQuery,
  EntryFlowStatsAggregateDetail,
  EntryFlowStatsAggregateFilters,
  EntryFlowStatsDetail,
  EntryFlowStatsListOptions
} from './entry-flow-stats.types';

export class EntryFlowStatsService {
  list(opts: EntryFlowStatsListOptions = {}): Promise<CollectionList<'entry-flow-stats'>> {
    const { filters, pagination } = opts;

    return axios
      .get<CollectionList<'entry-flow-stats'>>(`/purchases/entry-flow-stats`, {
        params: { ...filters, ...pagination }
      })
      .then(resp => resp?.data)
      .then(list => {
        if (!list) return list;
        /* Le tableau générique attend un champ id sur chaque ligne */
        return { ...list, values: (list.values || []).map(row => ({ ...row, id: row.purchase_id })) };
      });
  }

  get(purchaseId: number): Promise<EntryFlowStatsDetail> {
    return axios.get<EntryFlowStatsDetail>(`/purchases/${purchaseId}/entry-flow-stats`).then(resp => resp?.data);
  }

  /* Détail agrégé sur toutes les listes correspondant aux filtres (borné par défaut aux 90 derniers jours de réception côté backend) */
  getAggregate(
    filters: EntryFlowStatsAggregateFilters = {},
    cancelToken?: CancelToken
  ): Promise<EntryFlowStatsAggregateDetail> {
    return axios
      .get<EntryFlowStatsAggregateDetail>(`/purchases/entry-flow-stats/aggregate`, { params: filters, cancelToken })
      .then(resp => resp?.data);
  }

  /* Onglet Devices : chargé à la demande, paginé et filtré côté serveur (50 par défaut) */
  getDevices(query: EntryFlowDevicesQuery = {}, cancelToken?: CancelToken): Promise<EntryFlowDevicesPage> {
    return axios
      .get<EntryFlowDevicesPage>(`/purchases/entry-flow-stats/devices`, { params: query, cancelToken })
      .then(resp => resp?.data);
  }

  /* Export XLSX (3 onglets : Post-grading / Post process contrôle / Actuel) des tableaux
   * de répartition de la vue agrégée, généré côté backend (mêmes filtres que getAggregate) */
  downloadAggregateXlsx(filters: EntryFlowStatsAggregateFilters = {}): Promise<Blob> {
    return axios
      .get(`/purchases/entry-flow-stats/aggregate-xlsx`, { params: filters, responseType: 'blob' })
      .then(resp => resp?.data);
  }

  /* Requis par le contrat du tableau générique (liveEditItem) : collection en lecture seule */
  update(): Promise<never> {
    return Promise.reject(new Error('Les statistiques du flux d’entrée sont en lecture seule'));
  }

  /* Délais en heures décimales : affichage en base 60 (Xh MMmin), en jours + heures
   * au-delà de 48 h. Accepte aussi les strings (BigDecimal Rails sérialisé en JSON). */
  formatHours(hours: number | string | null | undefined): string {
    if (hours === null || hours === undefined) return '—';
    const value = Number(hours);
    if (isNaN(value)) return '—';
    const totalMinutes = Math.round(value * 60);
    const minutesPerDay = 24 * 60;
    if (value > 48) {
      const days = Math.floor(totalMinutes / minutesPerDay);
      const remainingHours = Math.round((totalMinutes % minutesPerDay) / 60);
      return `${days}j ${remainingHours}h`;
    }
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return m === 0 ? `${h}h` : `${h}h ${m.toString().padStart(2, '0')}min`;
  }

  formatPrice(value: number | string | null | undefined): string {
    if (value === null || value === undefined) return '—';
    const amount = Number(value);
    if (isNaN(amount)) return '—';
    return `${amount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  }
}

export const entryFlowStatsService = new EntryFlowStatsService();
